import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Img from '../../../Img';
import { LeaderboardParamValues, RANK_THRESHOLD } from '../constants';
import { trackEvent } from '../../../Analytics/TrackedClick';
import { SubscriptionTypes, ProfileTypes, LeaderboardTrackingData } from '../../../Analytics/constants';

import styles from '../localizedRankings.scss';

const RankingRow = ({ ranking, showSingles, showDoubles, onRankClick, onSegmentClick }) => {
    const { SINGLES_TENNIS, DOUBLES_TENNIS } = LeaderboardParamValues;
    const currentProfileId = useSelector((state) => state.profile?.playerId);
    const isPowerUser = useSelector((state) => state.auth?.user?.isPowered);
    const currentUserPlayerId = useSelector((state) => state.auth?.user?.claimedPlayer?.playerId);
    const isCurrentUser = parseInt(currentUserPlayerId) === parseInt(currentProfileId);

    const trackLeaderboardClick = (leaderboardType) => {
        trackEvent(LeaderboardTrackingData.TITLE, {
            subscriptionType: isPowerUser ? SubscriptionTypes.POWER : SubscriptionTypes.FREE,
            playerProfileId: parseInt(currentProfileId),
            profileType: isCurrentUser ? ProfileTypes.SELF : ProfileTypes.OTHER,
            geo: ranking?.subcategory,
            segment: ranking?.formattedCategory,
            leaderboardType,
        });
    };
    const handleRankClick = (rankingType) => {
        const updatedRanking = {
            ...ranking,
            rankingTypeClicked: rankingType,
            showDoubles,
            showSingles,
        };
        onRankClick(updatedRanking);
        trackLeaderboardClick(LeaderboardTrackingData.CLOSE_IN_RANK_TYPE);
    };

    const handleOnSegmentClick = () => {
        const updatedRanking = {
            ...ranking,
            showDoubles,
            showSingles,
        };
        onSegmentClick(updatedRanking);
        trackLeaderboardClick(LeaderboardTrackingData.TOP_ONE_HUNDRED_TYPE);
    };

    const WowChange = ({ wowChange }) => {
        if (wowChange === null || wowChange === undefined) {
            return null;
        }

        const isPositive = wowChange > 0;
        const isNegative = wowChange < 0;
        const absValue = Math.abs(wowChange);

        if (!isPositive && !isNegative) {
            return null;
        }

        return (
            <div className={`${styles.wowChangeContainer} ${isPositive ? styles.wowChangePositive : styles.wowChangeNegative}`}>
                <Img
                    src={isPositive ? '/img/v4/icon-up-arrow.svg' : '/img/v4/icon-down-arrow.svg'}
                />
                <span className={styles.wowChangeText}>
                    {isPositive ? '+' : '-'}{absValue?.toLocaleString()}
                </span>
            </div>
        );
    };

    const Rank = ({ rank, onClick, wowChange }) => {
        const rankContent = (
            <>
                <p className={rank >= RANK_THRESHOLD ? styles.rank : styles.clickableRank} data-testid="profile.local-rankings.rank-row.rank">
                    {rank?.toLocaleString()}
                </p>
                <WowChange wowChange={wowChange} />
            </>
        );

        if (rank >= RANK_THRESHOLD) {
            return (
                <div className={styles.rankingContainer}>
                    {rankContent}
                </div>
            );
        }
        return (
            <button onClick={onClick} className={styles.rankingContainer} type="button">
                {rankContent}
            </button>
        );
    };

    return (
        <div className={styles.rankingRow} data-testid="profile.local-rankings.rank-row">
            <div className={styles.rankingCategory}>
                <button onClick={handleOnSegmentClick} className={styles.formattedCategoryButton} type="button" data-testid="profile.local-rankings.rank-row.location">
                    {ranking?.formattedCategory}
                </button>
            </div>
            {ranking?.isLocked ? (
                <div className={styles.rankingsWrapper}>
                    {showSingles && (
                        <button
                            className={styles.rankingButton}
                            onClick={() => handleRankClick(SINGLES_TENNIS)}
                            type="button"
                        >
                            <Img src="/img/v2/icons/icon-feature-lock.svg" />
                        </button>
                    )}
                    {showDoubles && (
                        <button
                            className={styles.rankingButton}
                            onClick={() => handleRankClick(DOUBLES_TENNIS)}
                            type="button"
                        >
                            <Img src="/img/v2/icons/icon-feature-lock.svg" />
                        </button>
                    )}
                </div>
            ) : (
                <div className={styles.rankingsWrapper}>
                    {showSingles && (
                        <Rank
                            rank={ranking?.singlesRank}
                            onClick={() => handleRankClick(SINGLES_TENNIS)}
                            wowChange={ranking?.singlesWowChange}
                        />
                    )}
                    {showDoubles && (
                        <Rank
                            rank={ranking?.doublesRank}
                            onClick={() => handleRankClick(DOUBLES_TENNIS)}
                            wowChange={ranking?.doublesWowChange}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

RankingRow.propTypes = {
    ranking: PropTypes.shape({
        formattedCategory: PropTypes.string.isRequired,
        isLocked: PropTypes.bool.isRequired,
        singlesDisplayRank: PropTypes.string,
        doublesDisplayRank: PropTypes.string,
        singlesRank: PropTypes.number,
        doublesRank: PropTypes.number,
        singlesWowChange: PropTypes.number,
        doublesWowChange: PropTypes.number,
    }).isRequired,
    showSingles: PropTypes.bool.isRequired,
    showDoubles: PropTypes.bool.isRequired,
    onRankClick: PropTypes.func.isRequired,
    onSegmentClick: PropTypes.func.isRequired,
};

export default RankingRow;
